/**
 * MONSTER TOWN — NEIGHBOUR ROSTER. NPC CREW (src/npc/**).
 *
 * Who lives here. DATA: a new neighbour archetype is one entry in `ARCHETYPES`.
 *
 * Names are decoration only — LAW #4 means nothing in the game requires reading
 * one. They exist so voice lines and the photo mode have something to say later,
 * and so a grown-up can say "oh, that's Bramble" when the kid points at them.
 */

import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import type { MonsterConfig, MonsterBodyShape, MonsterEyes, MonsterHorns, MonsterPattern } from '../core/types'

export interface Archetype {
  readonly name: string
  readonly shape: MonsterBodyShape
  readonly color: PaletteName
  readonly accentColor: PaletteName
  readonly eyes: MonsterEyes
  readonly horns: MonsterHorns
  readonly pattern: MonsterPattern
  readonly hat: string | null
  /** Nudges the brain: what this neighbour likes doing most. */
  readonly likes: 'wander' | 'dance' | 'nap' | 'admire' | 'visit'
  /**
   * WHICH DAY THEY LIVE. The id of a `RoutineTable` in
   * `src/content/routines.ts` — a plain string on purpose, so the roster names
   * a rhythm without this crew importing the content that describes it.
   *
   * An id with no table is not an error: `routineAt()` returns null and the
   * neighbour simply potters about the way they always did (LAW #2 — a data
   * typo can cost a kid a schedule, never a neighbour).
   */
  readonly routine: string
}

/**
 * Roles a neighbour can live. Ids only — the tables are data in
 * `src/content/routines.ts` and this crew never reads them.
 */
export const ROUTINE_TOWNSFOLK = 'role_townsfolk'
export const ROUTINE_EARLYBIRD = 'role_earlybird'
export const ROUTINE_NIGHTOWL = 'role_nightowl'
export const ROUTINE_SHOPKEEPER = 'role_shopkeeper'
export const ROUTINE_SCOOPER = 'role_scooper'
export const ROUTINE_SERVER = 'role_server'
export const ROUTINE_ZOOKEEPER = 'role_zookeeper'

/** The rhythms an off-roster neighbour is dealt, in order. */
const ROUTINE_ROTATION: readonly string[] = [
  ROUTINE_TOWNSFOLK,
  ROUTINE_EARLYBIRD,
  ROUTINE_TOWNSFOLK,
  ROUTINE_NIGHTOWL,
]

export const ARCHETYPES: readonly Archetype[] = [
  {
    name: 'Bramble',
    shape: 'chunky',
    color: 'monsterLime',
    accentColor: 'monsterCream',
    eyes: 'two',
    horns: 'antlers',
    pattern: 'spots',
    hat: null,
    likes: 'wander',
    routine: ROUTINE_TOWNSFOLK,
  },
  {
    name: 'Pip',
    shape: 'blob',
    color: 'monsterBubblegum',
    accentColor: 'monsterBelly',
    eyes: 'three',
    horns: 'antennae',
    pattern: 'belly',
    hat: 'party',
    likes: 'dance',
    routine: ROUTINE_NIGHTOWL,
  },
  {
    name: 'Moss',
    shape: 'round',
    color: 'monsterTeal',
    accentColor: 'monsterCream',
    eyes: 'sleepy',
    horns: 'nubs',
    pattern: 'stripes',
    hat: null,
    likes: 'nap',
    routine: ROUTINE_EARLYBIRD,
  },
  {
    name: 'Tuffet',
    shape: 'tall',
    color: 'monsterLilac',
    accentColor: 'flowerPink',
    eyes: 'two',
    horns: 'curved',
    pattern: 'star',
    hat: 'sunhat',
    likes: 'admire',
    routine: ROUTINE_TOWNSFOLK,
  },
  {
    name: 'Dot',
    shape: 'blob',
    color: 'monsterSunny',
    accentColor: 'flowerWhite',
    eyes: 'one',
    horns: 'none',
    pattern: 'spots',
    hat: 'bow',
    likes: 'visit',
    routine: ROUTINE_SHOPKEEPER,
  },
  {
    name: 'Nimbus',
    shape: 'round',
    color: 'monsterSky',
    accentColor: 'flowerWhite',
    eyes: 'two',
    horns: 'antennae',
    pattern: 'belly',
    hat: 'beanie',
    likes: 'wander',
    routine: ROUTINE_TOWNSFOLK,
  },
  {
    name: 'Sorrel',
    shape: 'chunky',
    color: 'monsterCoral',
    accentColor: 'monsterCream',
    eyes: 'two',
    horns: 'nubs',
    pattern: 'stripes',
    hat: 'chef',
    likes: 'visit',
    routine: ROUTINE_SERVER,
  },
  {
    name: 'Bubble',
    shape: 'blob',
    color: 'monsterCream',
    accentColor: 'flowerPurple',
    eyes: 'three',
    horns: 'curved',
    pattern: 'star',
    hat: 'crown',
    likes: 'dance',
    routine: ROUTINE_SCOOPER,
  },
  {
    name: 'Fen',
    shape: 'tall',
    color: 'monsterLime',
    accentColor: 'wallBlue',
    eyes: 'sleepy',
    horns: 'antlers',
    pattern: 'plain',
    hat: null,
    likes: 'admire',
    routine: ROUTINE_ZOOKEEPER,
  },
  {
    name: 'Wobble',
    shape: 'round',
    color: 'monsterCoral',
    accentColor: 'flowerYellow',
    eyes: 'one',
    horns: 'antennae',
    pattern: 'spots',
    hat: 'flower',
    likes: 'nap',
    routine: ROUTINE_NIGHTOWL,
  },
]

export function archetypeLook(a: Archetype): MonsterConfig {
  return {
    shape: a.shape,
    color: a.color,
    accentColor: a.accentColor,
    eyes: a.eyes,
    horns: a.horns,
    pattern: a.pattern,
    hat: a.hat,
    outfit: null,
  }
}

/**
 * A neighbour who is not on the list — used once the town outgrows the roster,
 * so the population never repeats itself into looking like a clone army.
 */
export function randomArchetype(rng: () => number, index: number): Archetype {
  const shapes: MonsterBodyShape[] = ['blob', 'tall', 'round', 'chunky']
  const eyes: MonsterEyes[] = ['two', 'one', 'three', 'sleepy']
  const horns: MonsterHorns[] = ['none', 'nubs', 'curved', 'antlers', 'antennae']
  const patterns: MonsterPattern[] = ['plain', 'spots', 'stripes', 'belly', 'star']
  const accents: PaletteName[] = ['monsterBelly', 'monsterCream', 'flowerPink', 'flowerYellow', 'wallBlue']
  const likes: Archetype['likes'][] = ['wander', 'dance', 'nap', 'admire', 'visit']
  const pick = <T>(list: readonly T[], fallback: T): T => list[Math.floor(rng() * list.length)] ?? fallback
  return {
    name: `Neighbour ${index}`,
    shape: pick(shapes, 'blob'),
    color: pick(MONSTER_BODY_COLORS, 'monsterTeal'),
    accentColor: pick(accents, 'monsterBelly'),
    eyes: pick(eyes, 'two'),
    horns: pick(horns, 'nubs'),
    pattern: pick(patterns, 'belly'),
    hat: null,
    likes: pick(likes, 'wander'),
    routine: routineForName(`Neighbour ${index}`),
  }
}

/**
 * THE RHYTHM A NEIGHBOUR KEEPS FOR EVER, from their name alone.
 *
 * Deliberately derived rather than stored: the save section holds the name, so a
 * neighbour restored from a town built before routines existed still wakes up at
 * the same time tomorrow as they did today, and no save had to change to give
 * them a life. An off-roster monster is dealt one of the ordinary rhythms from a
 * hash of their name — stable, and never the whole town on one schedule.
 */
export function routineForName(name: string): string {
  const known = ARCHETYPES.find((a) => a.name === name)
  if (known !== undefined) return known.routine
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return ROUTINE_ROTATION[h % ROUTINE_ROTATION.length] ?? ROUTINE_TOWNSFOLK
}

/** Deterministic pick of the `n`th neighbour to move in. */
export function archetypeFor(index: number, rng: () => number): Archetype {
  const a = ARCHETYPES[index % ARCHETYPES.length]
  if (index < ARCHETYPES.length && a !== undefined) return a
  return randomArchetype(rng, index + 1)
}
